import {
  ListenerMiddleware,
  MiddlewareAPI,
  PayloadAction,
  ThunkDispatch,
  UnknownAction,
} from '@reduxjs/toolkit';
import { toast } from 'sonner';

import { baseApi } from 'api/baseApi';
import { GetItemDetailsResponse } from 'types/api/collaborate';
import { SitesAcrossWorkspacesResponse } from 'types/api/site';
import { UserResponse } from 'types/api/user';
import {
  getHostAndPath,
  isInChromeExtension,
  isInIframe,
} from 'utils/proxyUtils';
import { collaborateUrl } from '../utils/collaborateUrl';

import { initializeRealtime, presenceService } from '../realtime';
import { RootState } from '../store';
import { bootstrapAction, ready } from '../store/bootstrapSlice';
import {
  SelectedSiteInfo,
  selectSite,
  setCollaborateInfo,
  setEnabledCollaboration,
  setMatchingSites,
  setShowSiteSelectionModal,
  setSiteId,
  setSiteTotalTasks,
} from '../store/siteInfoSlice';
import { setSelectedTaskId } from '../store/tasksSlice';
import {
  resetUserDetails,
  setAuthenticationFailed,
  setLoggedIn,
  setUserDetails,
} from '../store/userSlice';

import 'api/collaborateApi';
import 'api/loginApi';
import 'api/siteApi';
import 'api/userApi';
import {
  setPageThumbnail,
  setProgress,
  startLoading,
  stopLoading,
} from '../store/uiSlice';

interface AuthConfig {
  token: string | null;
  timeout: number;
}

interface MessageEvent {
  data: {
    type: string;
    token?: string;
    siteId?: string;
  };
  origin?: string;
}

interface ChromeExtensionResponse {
  siteId?: string;
  error?: string;
}

const TIMEOUTS = {
  PARENT_TOKEN: 10000,
  PARENT_SITE_ID: 3000,
} as const;

const MESSAGE_TYPES = {
  AUTH_TOKEN_REQUEST: 'AUTH_TOKEN_REQUEST',
  AUTH_TOKEN_RESPONSE: 'AUTH_TOKEN_RESPONSE',
  SITE_ID_REQUEST: 'SITE_ID_REQUEST',
  SITE_ID_RESPONSE: 'SITE_ID_RESPONSE',
} as const;

const CHROME_EXTENSION_MESSAGE_TYPES = {
  SITE_ID_REQUEST: 'SITE_ID_REQUEST',
} as const;

class BootstrapProgressTracker {
  private api: MiddlewareAPI<
    ThunkDispatch<unknown, unknown, UnknownAction>,
    unknown
  >;
  private totalSteps: number = 0;
  private completedSteps: number = 0;

  constructor(
    api: MiddlewareAPI<ThunkDispatch<unknown, unknown, UnknownAction>, unknown>
  ) {
    this.api = api;
  }

  start(totalSteps: number = 4) {
    this.totalSteps = totalSteps;
    this.completedSteps = 0;
    this.api.dispatch(startLoading());
    this.api.dispatch(setProgress(0));
  }

  step(_description?: string) {
    this.completedSteps++;
    const progress = Math.round((this.completedSteps / this.totalSteps) * 100);
    this.api.dispatch(setProgress(Math.min(progress, 95))); // Don't hit 100% until complete
    console.log(`Bootstrap progress: ${_description} (${progress}%)`);
  }

  complete() {
    this.api.dispatch(setProgress(100));
    // Small delay to show 100% before hiding
    setTimeout(() => {
      this.api.dispatch(stopLoading());
    }, 200);
  }

  error() {
    this.api.dispatch(stopLoading());
  }
}

const getScriptSiteId = (): string | null => {
  const injectedScript = document.querySelector('script[data-siteid]');
  return injectedScript?.getAttribute('data-siteid') || null;
};

const getSearchParams = () =>
  Object.fromEntries(new URLSearchParams(window.location.search));

const removeAtarimCollabParam = (): void => {
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has('atarimcollab')) {
    searchParams.delete('atarimcollab');
    const newUrl = `${window.location.pathname}${searchParams.size ? '?' + searchParams.toString() : ''}`;
    window.history.replaceState({}, document.title, newUrl);
  }
};

const setupTokenListener = (
  callback: (token: string | null) => void,
  timeout: number = TIMEOUTS.PARENT_TOKEN
): NodeJS.Timeout => {
  let timeoutId: NodeJS.Timeout | null = null;
  let resolved = false;

  const handleMessage = (event: MessageEvent): void => {
    if (resolved) return;

    if (
      event.data?.type === MESSAGE_TYPES.AUTH_TOKEN_RESPONSE &&
      event.data.token
    ) {
      resolved = true;
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('message', handleMessage);
      callback(event.data.token);
    }
  };

  timeoutId = setTimeout(() => {
    if (!resolved) {
      resolved = true;
      window.removeEventListener('message', handleMessage);
      callback(null);
    }
  }, timeout);

  window.addEventListener('message', handleMessage);
  window.parent?.postMessage({ type: MESSAGE_TYPES.AUTH_TOKEN_REQUEST }, '*');

  return timeoutId;
};

const authenticateUser = async (
  api: MiddlewareAPI<ThunkDispatch<unknown, unknown, UnknownAction>, unknown>,
  config: AuthConfig,
  progressTracker: BootstrapProgressTracker
): Promise<void> => {
  progressTracker.step('Authenticating user');

  api.dispatch(
    // @ts-expect-error Endpoints are injected
    baseApi.endpoints.authenticate.initiate({ token: config.token })
  );
};

const setupSiteIdListener = (
  callback: (siteId: string | null) => void,
  timeout: number = TIMEOUTS.PARENT_SITE_ID
): void => {
  let timeoutId: NodeJS.Timeout | null = null;
  let resolved = false;

  const handleMessage = (event: MessageEvent): void => {
    if (resolved) return;

    if (
      event.data?.type === MESSAGE_TYPES.SITE_ID_RESPONSE &&
      event.data.siteId
    ) {
      resolved = true;
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('message', handleMessage);
      callback(event.data.siteId);
    }
  };

  timeoutId = setTimeout(() => {
    if (!resolved) {
      resolved = true;
      window.removeEventListener('message', handleMessage);
      callback(null);
    }
  }, timeout);

  window.addEventListener('message', handleMessage);
  window.parent?.postMessage({ type: MESSAGE_TYPES.SITE_ID_REQUEST }, '*');
};

const getChromeExtensionSiteId = async (): Promise<string | null> => {
  try {
    const response = (await chrome.runtime.sendMessage({
      type: CHROME_EXTENSION_MESSAGE_TYPES.SITE_ID_REQUEST,
      url: window.location.hostname,
    })) as ChromeExtensionResponse;

    if (response?.siteId) {
      return response.siteId;
    }

    return null;
  } catch (error) {
    console.error('Error fetching site ID from Chrome extension:', error);
    return null;
  }
};

const findSite = async (
  api: MiddlewareAPI<ThunkDispatch<unknown, unknown, UnknownAction>, unknown>,
  progressTracker: BootstrapProgressTracker
): Promise<void> => {
  progressTracker.step('Finding site');

  const providedSiteId = new URL(window.location.toString()).searchParams.get(
    'siteId'
  );

  if (isInIframe() && window.parent) {
    return new Promise((resolve) => {
      setupSiteIdListener((siteId) => {
        const finalSiteId = siteId || providedSiteId || getScriptSiteId();
        proceedWithSiteId(api, finalSiteId, progressTracker);
        resolve();
      });
    });
  }

  if (isInChromeExtension()) {
    const extensionSiteId = await getChromeExtensionSiteId();
    const finalSiteId = extensionSiteId || providedSiteId || getScriptSiteId();
    proceedWithSiteId(api, finalSiteId, progressTracker);
    return;
  }

  proceedWithSiteId(api, providedSiteId || getScriptSiteId(), progressTracker);
};

const proceedWithSiteId = (
  api: MiddlewareAPI<ThunkDispatch<unknown, unknown, UnknownAction>, unknown>,
  siteId: string | null,
  progressTracker: BootstrapProgressTracker
): void => {
  if (siteId && siteId !== 'null') {
    progressTracker.step('Loading site details');
    api.dispatch(setSiteId(siteId));
    api.dispatch(ready('success'));
    api.dispatch(
      // @ts-expect-error Endpoints are injected
      baseApi.endpoints.getItemDetails.initiate({
        id: siteId,
        proxyMethod: 'injectable_script',
        forceRefetch: false,
      })
    );
    return;
  }

  const state = api.getState() as RootState;
  const activationCallback = new URL(
    window.location.toString()
  ).searchParams.get('activation_callback');

  if (isInChromeExtension()) {
    progressTracker.complete();
    api.dispatch(ready('site-selection'));
    api.dispatch(setShowSiteSelectionModal(true));
    return;
  }

  if (!state.user.loggedIn) {
    if (activationCallback && !isInIframe()) {
      progressTracker.complete();
      api.dispatch(ready('plugin_activation'));
      return;
    }
    handleNoSiteIdError(api, progressTracker);
    return;
  }

  if (activationCallback && !isInIframe()) {
    progressTracker.complete();
    api.dispatch(ready('plugin_activation'));
    return;
  }

  progressTracker.error();
  toast.error(
    'No site ID found. Please contact the site owner to adjust the Atarim install.'
  );
  api.dispatch(ready('error'));
};

const handleNoSiteIdError = (
  api: MiddlewareAPI<ThunkDispatch<unknown, unknown, UnknownAction>, unknown>,
  progressTracker: BootstrapProgressTracker
): void => {
  progressTracker.error();
  window.localStorage.removeItem('atarimGuestUser');
  api.dispatch(setLoggedIn(false));
  api.dispatch(resetUserDetails());
  toast.error(
    'No site found. Please contact the site owner to adjust the Atarim install.'
  );
  api.dispatch(ready('error'));
  removeAtarimCollabParam();
};

const handleUserSuccess = (
  api: MiddlewareAPI<ThunkDispatch<unknown, unknown, UnknownAction>, unknown>,
  action: PayloadAction<UserResponse>,
  progressTracker: BootstrapProgressTracker
): void => {
  progressTracker.step('User authenticated successfully');

  api.dispatch(setLoggedIn(true));
  api.dispatch(setUserDetails(action.payload));
  // Make sure Echo is authenticated with fresh cookies/token
  // Reinitialize the Echo connection so presence authorizer picks up new auth
  // Intentionally fire-and-forget here
  initializeRealtime().then(() => {
    presenceService.forceReauthorizeAll();
  });
};

const handleAtarimGuestUser = (
  api: MiddlewareAPI<ThunkDispatch<unknown, unknown, UnknownAction>, unknown>,
  progressTracker: BootstrapProgressTracker
): void => {
  const storedGuestUser = window.localStorage.getItem('atarimGuestUser');

  try {
    progressTracker.step('Setting up guest user');
    const guestData = storedGuestUser ? JSON.parse(storedGuestUser) : null;
    api.dispatch(setLoggedIn(true));
    // @ts-expect-error Endpoints are injected
    api.dispatch(setUserDetails({ data: guestData }));
    api.dispatch(ready('success'));
    findSite(api, progressTracker);
  } catch (_: unknown) {
    progressTracker.error();
    api.dispatch(setLoggedIn(false));
    api.dispatch(ready('error'));
  }
};

const handleCollaborateSiteSuccess = (
  api: MiddlewareAPI<ThunkDispatch<unknown, unknown, UnknownAction>, unknown>,
  action: PayloadAction<GetItemDetailsResponse>,
  progressTracker: BootstrapProgressTracker
): void => {
  progressTracker.step('Site details loaded successfully');

  const { result } = action.payload;

  api.dispatch(setCollaborateInfo(action.payload));

  const selectedSiteInfo: SelectedSiteInfo = {
    id: result.id,
    name: result.name,
    users: result.users.map((u) => ({
      avatar: u.avatar,
      email: u.email,
      name: u.label,
      normal_id: u.value,
    })),
    workspace_id: result.workspace_id,
    setting: {
      whiteLabel: {
        favicon: result?.setting?.whitelabel.favicon,
        logo_link: result?.setting?.whitelabel.logo_link,
        logo: result?.setting?.whitelabel.logo,
      },
      enabled_collaboration: result?.setting?.enabled_collaboration,
    },
  };

  api.dispatch(
    setEnabledCollaboration(result?.setting?.enabled_collaboration || false)
  );
  api.dispatch(selectSite(selectedSiteInfo));
  api.dispatch(setSiteTotalTasks(result.total_tasks));

  // @ts-expect-error Endpoints are injected
  api.dispatch(baseApi.endpoints.getPages.initiate({ siteId: result.id }));
  api.dispatch(
    // @ts-expect-error Endpoints are injected
    baseApi.endpoints.getAllMilestones.initiate({ siteId: result.id })
  );

  const workspaceId = result.workspace_id;
  const userState = (api.getState() as RootState).user;
  const loginUser = userState?.loggedIn || null;
  if (!isInIframe() && workspaceId && loginUser) {
    api.dispatch(
      // @ts-expect-error Endpoints are injected
      baseApi.endpoints.getUser.initiate()
    );
  }

  progressTracker.complete();
  api.dispatch(ready('success'));
};

const handleSiteSuccess = (
  api: MiddlewareAPI<ThunkDispatch<unknown, unknown, UnknownAction>, unknown>,
  action: PayloadAction<SitesAcrossWorkspacesResponse>,
  progressTracker: BootstrapProgressTracker
): void => {
  const pageUrl = getHostAndPath(collaborateUrl())?.host;
  const activationCallback = new URL(
    window.location.toString()
  ).searchParams.get('activation_callback');

  if (!pageUrl) {
    return;
  }

  const matchingSites = action.payload.filter((site) =>
    site.url_host.match(pageUrl)
  );

  if (matchingSites.length !== 1) {
    if (matchingSites.length > 1) {
      api.dispatch(setMatchingSites(matchingSites));
    }
    if ((api.getState() as RootState).user.loggedIn) {
      if (activationCallback) return;
      progressTracker.complete();
      api.dispatch(setShowSiteSelectionModal(true));
    }
    return;
  }

  const site = matchingSites[0];

  if (!site?.site_id) {
    return;
  }

  const selectedSiteInfo: SelectedSiteInfo = {
    id: site.site_id,
    name: site.name,
    users: [],
    setting: {
      whiteLabel: {
        favicon: site.favicon ?? '',
        logo_link: null,
        logo: null,
      },
      enabled_collaboration: true,
    },
    workspace_id: site.workspace_id,
  };

  api.dispatch(selectSite(selectedSiteInfo));
  api.dispatch(setPageThumbnail(site.thumbnail_s3_url));
  api.dispatch(
    // @ts-expect-error Endpoints are injected
    baseApi.endpoints.getItemDetails.initiate({
      id: site.site_id,
      reload: false,
      proxyMethod: 'injectable_script',
    })
  );
};

const handleInviteToken = async (
  api: MiddlewareAPI<ThunkDispatch<unknown, unknown, UnknownAction>, unknown>,
  inviteToken: string,
  authInitiatedRef: { current: boolean },
  progressTracker: BootstrapProgressTracker
) => {
  try {
    progressTracker.step('Processing invitation');

    const workspaceId = (api.getState() as RootState).siteInfo.selectedSiteInfo
      ?.workspace_id;
    try {
      // @ts-expect-error Endpoints are injected
      await api.dispatch(baseApi.endpoints.logout.initiate()).unwrap();
    } catch (logoutError) {
      console.error('Error during logout:', logoutError);
      // Already logged out, continue with invite acceptance
    }

    const acceptInviteResult = await api
      // @ts-expect-error Endpoints are injected
      .dispatch(baseApi.endpoints.acceptInvite.initiate({ token: inviteToken }))
      .unwrap();

    if (!acceptInviteResult?.status) {
      progressTracker.error();
      toast.error(
        'Failed to accept invitation. Please try again or contact support.'
      );
      const url = new URL(window.location.href);
      url.searchParams.delete('token');
      window.history.replaceState({}, '', url.toString());
      return;
    }

    progressTracker.step('Invitation accepted, authenticating');
    const authToken = acceptInviteResult?.result?.access_token || null;

    await api
      // @ts-expect-error Endpoints are injected
      .dispatch(baseApi.endpoints.authenticate.initiate({ token: authToken }))
      .unwrap();
    authInitiatedRef.current = true;

    const userResponse = await api
      .dispatch(
        // @ts-expect-error Endpoints are injected
        baseApi.endpoints.getUser.initiate({ workspace_id: workspaceId })
      )
      .unwrap();

    api.dispatch(setUserDetails(userResponse));

    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    window.history.replaceState({}, '', url.toString());

    progressTracker.complete();
    toast.success('Invitation accepted successfully!');
  } catch (error) {
    console.error('Error accepting invite:', error);
    progressTracker.error();

    try {
      await api
        // @ts-expect-error Endpoints are injected
        .dispatch(baseApi.endpoints.authenticate.initiate({ token: null }))
        .unwrap();
    } catch (authError) {
      console.error('Error during authentication after invite:', authError);
      api.dispatch(setLoggedIn(false));
      api.dispatch(resetUserDetails());
    }
    toast.error(
      'Failed to accept invitation. Please try again or contact support.'
    );
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    window.history.replaceState({}, '', url.toString());
  }
};

export const bootstrapMiddleware: ListenerMiddleware =
  (api) => (next) => async (action) => {
    const progressTracker = new BootstrapProgressTracker(api);

    if (bootstrapAction.match(action)) {
      // Start loading with estimated steps
      const inviteToken = new URL(window.location.toString()).searchParams.get(
        'token'
      );
      const totalSteps = inviteToken ? 6 : 4; // More steps for invite flow
      progressTracker.start(totalSteps);

      // if (isInChromeExtension()) {
      //   progressTracker.step('Initializing Chrome extension');
      //   api.dispatch(
      //     // @ts-expect-error DO NOT REMOVE/MOVE THIS LINE (chrome extension needs it)
      //     baseApi.endpoints.getUser.initiate()
      //   );
      //   return next(action);
      // }

      const authInitiatedRef = { current: false };

      const searchParams = getSearchParams();
      if (searchParams.taskid) {
        api.dispatch(setSelectedTaskId(searchParams.taskid));
      }

      const token = (api.getState() as RootState).user?.token;

      if (token) {
        await authenticateUser(
          api,
          { token, timeout: TIMEOUTS.PARENT_TOKEN },
          progressTracker
        );
      } else if (isInIframe() && window.parent) {
        setupTokenListener((receivedToken) => {
          authenticateUser(
            api,
            {
              token: receivedToken,
              timeout: TIMEOUTS.PARENT_TOKEN,
            },
            progressTracker
          );
        });
      } else {
        await authenticateUser(
          api,
          {
            token: null,
            timeout: TIMEOUTS.PARENT_TOKEN,
          },
          progressTracker
        );
      }

      if (inviteToken && !isInIframe()) {
        handleInviteToken(api, inviteToken, authInitiatedRef, progressTracker);
        return next(action);
      }

      return next(action);
    }

    // @ts-expect-error Endpoints are injected
    if (baseApi.endpoints.getUser.matchFulfilled(action)) {
      const progressTracker = new BootstrapProgressTracker(api);
      handleUserSuccess(
        api,
        action as PayloadAction<UserResponse>,
        progressTracker
      );
      return next(action);
    }

    // @ts-expect-error Endpoints are injected
    if (baseApi.endpoints.getUser.matchRejected(action)) {
      const progressTracker = new BootstrapProgressTracker(api);
      const storedGuestUser = window.localStorage.getItem('atarimGuestUser');

      if (storedGuestUser) {
        handleAtarimGuestUser(api, progressTracker);
      } else {
        progressTracker.complete();
        api.dispatch(setLoggedIn(false));
        api.dispatch(ready('success'));
      }
      return next(action);
    }

    if (
      // @ts-expect-error Endpoints are injected
      baseApi.endpoints.authenticate.matchFulfilled(action)
    ) {
      api.dispatch(setLoggedIn(true));
      const iframeWorkspaceId = getSearchParams().workspaceId;
      const workspace_id =
        getSearchParams()?.workspace_id || iframeWorkspaceId || 0;

      const paramsSiteId = getSearchParams().siteId;
      let siteId = paramsSiteId || getScriptSiteId();
      if (isInChromeExtension()) {
        siteId = await getChromeExtensionSiteId();
      }
      proceedWithSiteId(api, siteId, progressTracker);

      api.dispatch(
        // @ts-expect-error Endpoints are injected
        baseApi.endpoints.getUser.initiate({ workspace_id })
      );
    }

    if (
      // @ts-expect-error Endpoints are injected
      baseApi.endpoints.authenticate.matchRejected(action)
      // &&
      // !isInChromeExtension()
    ) {
      const progressTracker = new BootstrapProgressTracker(api);
      const storedGuestUser = window.localStorage.getItem('atarimGuestUser');

      const activationCallback = new URL(
        window.location.toString()
      ).searchParams.get('activation_callback');
      if (activationCallback && !isInIframe()) {
        api.dispatch(setAuthenticationFailed(true));
      }

      if (storedGuestUser) {
        handleAtarimGuestUser(api, progressTracker);
      } else {
        api.dispatch(setLoggedIn(false));
        api.dispatch(resetUserDetails());
        findSite(api, progressTracker);
      }
    }

    // @ts-expect-error Endpoints are injected
    if (baseApi.endpoints.getItemDetails.matchFulfilled(action)) {
      const progressTracker = new BootstrapProgressTracker(api);
      api.dispatch(ready('success'));
      handleCollaborateSiteSuccess(
        api,
        action as PayloadAction<GetItemDetailsResponse>,
        progressTracker
      );
    }

    // @ts-expect-error Endpoints are injected
    if (baseApi.endpoints.getItemDetails.matchRejected(action)) {
      api.dispatch(stopLoading());
      api.dispatch(setSiteTotalTasks(0));
      api.dispatch(resetUserDetails());
      api.dispatch(setLoggedIn(false));
      api.dispatch(ready('error'));
      toast.error(
        'Failed to load site details. Please try again or contact support.'
      );
    }

    // @ts-expect-error Endpoints are injected
    if (baseApi.endpoints.getSitesAcrossWorkspaces.matchFulfilled(action)) {
      const progressTracker = new BootstrapProgressTracker(api);
      handleSiteSuccess(
        api,
        action as PayloadAction<SitesAcrossWorkspacesResponse>,
        progressTracker
      );
    }

    return next(action);
  };
